import React from "react";
import {
    Eye,
    Users,
    Heart,
    BookOpen,
    GraduationCap,
    BarChart3,
    X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// 6 equal segments with icon + hover lift + click-to-open definition overlay.
// SVG viewBox 500x500, center (250,250), outer radius 240, inner radius 90.
const DOMAINS = [
    {
        id: "vision",
        name: "Vision & Strategic Coherence",
        color: "#2E5D3A",
        Icon: Eye,
        definition:
            "The degree to which an organization's vision is clearly defined, collectively understood, and consistently translated into aligned actions, systems, decisions, and measurable outcomes across all levels of the organization.",
    },
    {
        id: "leadership",
        name: "Leadership & Systems Execution",
        color: "#0F7B8A",
        Icon: Users,
        definition:
            "Leadership & Systems Execution refers to the capacity of leaders at all levels to translate vision and strategy into coherent, aligned, and consistently implemented actions and systems that produce measurable outcomes.",
    },
    {
        id: "culture",
        name: "Culture & Climate",
        color: "#5B2B7B",
        Icon: Heart,
        definition:
            "Culture & Climate refers to the collective beliefs, behaviors, relationships, and conditions that shape how individuals experience the organization on a daily basis. It reflects the extent to which the environment is safe, inclusive, supportive, and purpose-driven, enabling both students and staff to perform at high levels.",
    },
    {
        id: "curriculum",
        name: "Curriculum & Assessment",
        color: "#3D2B7B",
        Icon: BookOpen,
        definition:
            "The degree to which standards-aligned curriculum, instructional materials, and assessment practices are intentionally designed, coherently aligned, and consistently implemented to ensure all students have access to rigorous learning and timely, actionable feedback.",
    },
    {
        id: "instructional",
        name: "Instructional Core",
        color: "#1B3A7B",
        Icon: GraduationCap,
        definition:
            "The quality and consistency of the interaction between teacher, student, and content that drives meaningful learning — moving beyond task completion and surface-level engagement to promote deep thinking, problem-solving, and independent learning.",
    },
    {
        id: "data",
        name: "Data Systems & Progress Monitoring",
        color: "#B8641F",
        Icon: BarChart3,
        definition:
            "Data Systems & Progress Monitoring refers to the extent to which an organization establishes, implements, and sustains coherent data structures and disciplined monitoring processes to track progress toward strategic goals, inform decision-making, and drive continuous improvement.",
    },
];

const CX = 250;
const CY = 250;
const R_OUT = 240;
const R_IN = 90;

function polar(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function segmentPath(startAngle, endAngle) {
    const large = endAngle - startAngle > 180 ? 1 : 0;
    const p1 = polar(CX, CY, R_OUT, startAngle);
    const p2 = polar(CX, CY, R_OUT, endAngle);
    const p3 = polar(CX, CY, R_IN, endAngle);
    const p4 = polar(CX, CY, R_IN, startAngle);
    return [
        `M ${p1.x} ${p1.y}`,
        `A ${R_OUT} ${R_OUT} 0 ${large} 1 ${p2.x} ${p2.y}`,
        `L ${p3.x} ${p3.y}`,
        `A ${R_IN} ${R_IN} 0 ${large} 0 ${p4.x} ${p4.y}`,
        "Z",
    ].join(" ");
}

export default function FrameworkWheel({ size = 520, className = "" }) {
    const [hover, setHover] = React.useState(null);
    const [selected, setSelected] = React.useState(null);
    const sweep = 360 / DOMAINS.length;
    const containerRef = React.useRef(null);

    // Close on outside click / Escape
    React.useEffect(() => {
        if (!selected) return;
        const handleKey = (e) => e.key === "Escape" && setSelected(null);
        const handleClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setSelected(null);
            }
        };
        document.addEventListener("keydown", handleKey);
        document.addEventListener("mousedown", handleClick);
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.removeEventListener("mousedown", handleClick);
        };
    }, [selected]);

    const selectedDomain = DOMAINS.find((d) => d.id === selected);

    return (
        <div
            ref={containerRef}
            className={`relative select-none ${className}`}
            style={{ width: size, maxWidth: "100%", aspectRatio: "1 / 1" }}
            data-testid="framework-wheel"
        >
            {/* Glow underlay */}
            <div
                aria-hidden
                className="absolute inset-0 rounded-full blur-3xl opacity-40"
                style={{
                    background:
                        "radial-gradient(circle at 50% 50%, rgba(138,196,255,0.55), rgba(30,86,160,0.2) 55%, transparent 70%)",
                }}
            />

            <svg
                viewBox="0 0 500 500"
                className="wheel-idle relative w-full h-full"
                role="img"
                aria-label="ESSS Framework Wheel"
            >
                <defs>
                    <filter id="wheel-shadow" x="-20%" y="-20%" width="140%" height="140%">
                        <feDropShadow
                            dx="0"
                            dy="6"
                            stdDeviation="8"
                            floodColor="#0B2545"
                            floodOpacity="0.35"
                        />
                    </filter>
                    <radialGradient id="center-gradient" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#FFFFFF" />
                        <stop offset="100%" stopColor="#D6E6F2" />
                    </radialGradient>
                </defs>

                {/* Outer thin ring */}
                <circle
                    cx={CX}
                    cy={CY}
                    r={R_OUT + 6}
                    fill="none"
                    stroke="rgba(255,255,255,0.35)"
                    strokeWidth="1"
                />

                {DOMAINS.map((d, i) => {
                    const start = i * sweep;
                    const end = start + sweep;
                    const mid = start + sweep / 2;
                    const iconPos = polar(CX, CY, (R_OUT + R_IN) / 2, mid);
                    const path = segmentPath(start, end);
                    const isHover = hover === d.id;
                    return (
                        <g
                            key={d.id}
                            className={`wheel-segment ${selected === d.id ? "selected" : ""}`}
                            onMouseEnter={() => setHover(d.id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelected((prev) => (prev === d.id ? null : d.id));
                            }}
                            data-testid={`wheel-segment-${d.id}`}
                        >
                            <path
                                d={path}
                                fill={d.color}
                                stroke="#0B2545"
                                strokeWidth="1.5"
                                opacity={hover && !isHover ? 0.72 : 1}
                                filter="url(#wheel-shadow)"
                            />
                            <foreignObject
                                x={iconPos.x - 18}
                                y={iconPos.y - 18}
                                width={36}
                                height={36}
                                style={{ pointerEvents: "none" }}
                            >
                                <div
                                    style={{
                                        width: 36,
                                        height: 36,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#FFFFFF",
                                    }}
                                >
                                    <d.Icon size={26} strokeWidth={2} />
                                </div>
                            </foreignObject>
                        </g>
                    );
                })}

                {/* Center circle */}
                <circle
                    cx={CX}
                    cy={CY}
                    r={R_IN - 4}
                    fill="url(#center-gradient)"
                    stroke="#0B2545"
                    strokeWidth="1.5"
                />
                <text
                    x={CX}
                    y={CY - 6}
                    textAnchor="middle"
                    fill="#0B2545"
                    fontFamily="Fraunces, serif"
                    fontSize="14"
                    fontWeight="700"
                    letterSpacing="1"
                >
                    ORGANIZATIONAL
                </text>
                <text
                    x={CX}
                    y={CY + 14}
                    textAnchor="middle"
                    fill="#0B2545"
                    fontFamily="Fraunces, serif"
                    fontSize="14"
                    fontWeight="700"
                    letterSpacing="1"
                >
                    COHERENCE
                </text>
            </svg>

            {/* Hover tooltip (only when not selected) */}
            {hover && !selected && (
                <div
                    className="absolute left-1/2 -translate-x-1/2 bottom-2 backdrop-blur-md bg-white/95 text-[#0B2545] px-4 py-2 rounded-full text-sm font-semibold shadow-lg border border-white/50 pointer-events-none"
                    data-testid={`wheel-tooltip-${hover}`}
                >
                    {DOMAINS.find((d) => d.id === hover)?.name}
                </div>
            )}

            {/* Click-open overlay panel */}
            <AnimatePresence>
                {selectedDomain && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.25 }}
                        className="absolute top-1/2 left-full -translate-y-1/2 ml-4 w-72 max-w-[85vw] rounded-2xl bg-white text-[#0B2545] shadow-2xl border border-[#D6E6F2] p-5 z-20"
                        style={{ borderLeft: `4px solid ${selectedDomain.color}` }}
                        data-testid={`wheel-panel-${selectedDomain.id}`}
                    >
                        <button
                            onClick={() => setSelected(null)}
                            className="absolute top-3 right-3 text-[#0E1B2C]/50 hover:text-[#0B2545]"
                            aria-label="Close"
                            data-testid={`wheel-panel-close-${selectedDomain.id}`}
                        >
                            <X size={16} />
                        </button>
                        <div className="flex items-center gap-2 mb-2">
                            <span
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg"
                                style={{ backgroundColor: selectedDomain.color, color: "#FFF" }}
                            >
                                <selectedDomain.Icon size={18} />
                            </span>
                            <div
                                className="text-[10px] uppercase tracking-[0.2em] font-bold"
                                style={{ color: selectedDomain.color }}
                            >
                                Domain
                            </div>
                        </div>
                        <div className="font-serif text-lg font-semibold leading-tight">
                            {selectedDomain.name}
                        </div>
                        <p className="mt-3 text-sm text-[#0E1B2C]/85 leading-relaxed">
                            {selectedDomain.definition}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export { DOMAINS };
